// **Modelle eines Mandanten** — was der Picker der Instanz über jedes Modell
// sagt (Beschreibung, Hersteller, Verbrauch), welche Aufwandsstufen es anbietet
// und womit es startet, und was es kostet. In einer verwalteten Instanz gibt es
// den Abschnitt „Modelle" nur hier (docs/11 6); gespeichert wird in der Instanz
// über die Management-API, mit ihren Regeln (`PUT /api/platform/models`).
//
// Preise: Für den Plattform-Key zählt Requesty die Kosten selbst; die Preise in
// der Instanz braucht `/status` und der eigene Endpoint des Kunden. „Preise aus
// dem Gateway übernehmen" füllt sie aus dem Katalog, soweit er welche nennt.
//
// Welches Modell aufgeklappt ist, steht in `?model=` — ein Neuladen behält es.

import { useQuery } from "@tanstack/react-query";
import { Fragment } from "react";
import { get, post } from "../../api/client";
import { keys } from "../../api/queries";
import type { InstanceModelsResponse, ModelPrice } from "../../api/types";
import { errorText } from "../../lib/labels";
import { useSearchParams, useSetSearch } from "../../router/router";
import { Loading, Note } from "../../ui/badge";
import { BusyButton } from "../../ui/busy-button";
import { Card } from "../../ui/card";
import { toast } from "../../ui/toaster";
import { ModelEditor } from "./model-editor";
import { DEFAULT_EFFORTS, EFFORT_NAME } from "./model-editor-map";
import type { TabProps } from "./tabs";

const money = (v: number | null | undefined) => (v === undefined || v === null ? "—" : `$${Number(v).toFixed(Number(v) < 10 ? 2 : 1)}`);

export function ModelsTab({ tenant: t }: TabProps) {
  const editing = useSearchParams().get("model");
  const setSearch = useSetSearch();
  const q = useQuery({ queryKey: keys.instanceModels(t.id), queryFn: () => get<InstanceModelsResponse>(`/tenants/${t.id}/instance/models`) });
  if (q.isPending) return <Card title="Modelle"><Loading /></Card>;
  if (q.isError) return <Card title="Modelle"><Note tone="error">Die Instanz antwortet nicht: {errorText(q.error)}</Note></Card>;
  const data = q.data;
  const unpriced = data.models.filter((m) => !data.prices[m.id]).length;

  const takePrices = async () => {
    try {
      const res = await post<{ set: string[]; missing: string[] }>(`/tenants/${t.id}/instance/models/prices-from-gateway`, {});
      toast(res.set.length
        ? `${res.set.length} Preise übernommen${res.missing.length ? `, ${res.missing.length} ohne Preis im Katalog` : ""}`
        : "Der Katalog nennt für diese Modelle keine Preise", res.set.length ? "" : "error");
      await q.refetch();
    } catch (err) { toast(errorText(err), "error"); }
  };

  return (
    <Card
      title="Modelle"
      hint="Was der Modell-Picker der Instanz über ein Modell sagt, welche Aufwandsstufen es anbietet und was es kostet (USD je 1 Mio. Tokens, Ein / Aus)."
      actions={unpriced ? <BusyButton className="pf-btn pf-btn--sm" onClick={takePrices}>Preise aus dem Gateway übernehmen ({unpriced} ohne Preis)</BusyButton> : null}
      flush
    >
      {data.models.length ? (
        <div className="pf-table-wrap">
          <table className="pf-table pf-table--compact">
            <thead><tr><th>Modell</th><th className="pf-hide-phone">Endpoint</th><th className="pf-hide-phone">Aufwand</th><th className="pf-num">Preis</th><th></th></tr></thead>
            <tbody>
              {data.models.map((m) => {
                const p = data.profiles[m.id] ?? {};
                const price: ModelPrice | undefined = data.prices[m.id];
                const offered = (p.efforts ?? DEFAULT_EFFORTS).filter((e) => m.effort_supported.includes(e));
                const open = editing === m.id;
                return (
                  <Fragment key={m.id}>
                    <tr className={open ? "pf-row--open" : ""}>
                      <td><span className="mono pf-break">{m.id}</span>{m.labels.length ? <div className="pf-sub">{m.labels.join(", ")}</div> : null}</td>
                      <td className="pf-hide-phone pf-sub">{m.endpoints.join(", ") || "—"}</td>
                      <td className="pf-hide-phone">
                        {m.effort
                          ? <>{EFFORT_NAME[p.effort ?? ""] ?? "Vorgabe"}<div className="pf-sub">{offered.map((e) => EFFORT_NAME[e]).join(" · ") || "keine Stufen"}</div></>
                          : <span className="pf-sub">—</span>}
                      </td>
                      <td className="pf-num">{price ? `${money(price.input)} / ${money(price.output)}` : <span className="pf-tag pf-tag--warn">kein Preis</span>}</td>
                      <td className="pf-num">
                        <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost" onClick={() => setSearch({ model: open ? null : m.id })}>{open ? "Schließen" : "Bearbeiten"}</button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="pf-row-editor">
                        <td colSpan={5}>
                          <ModelEditor
                            key={q.dataUpdatedAt} tenantId={t.id} model={m} profile={p} price={price}
                            descriptionMax={data.description_max} onSaved={() => q.refetch()}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <div className="pf-empty">Die Instanz kennt noch keine Modelle — erst einen Endpoint einrichten und übernehmen.</div>}
    </Card>
  );
}
